import Column from "@amzn/meridian/column";
import Divider from "@amzn/meridian/divider";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Theme from "@amzn/meridian/theme";
import { css } from "@emotion/css";
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import Breadcrumb, { BreadcrumbGroup } from "@amzn/meridian/breadcrumb";

import { ColorCodes } from "../constants/ColorCodes";
import { footerConstants, FooterModalData } from "../constants/Footer";
import { ILayoutProps } from "../types/Layout.types";
import { BreadcrumbTokens, FooterTokens } from "../utils/Tokens";
import FooterMobile from "./FooterMobile";
import { StatusProvider } from "./StatusProvider";

const { buttonBackgroundColorSecondaryDefault } = FooterTokens;
const {
  ANTI_FLASH_WHITE,
  OUTER_SPACE_GREY,
  WHITE,
  SILVER_SAND_GREY,
} = ColorCodes;
const { SMARTBIZ_BY_AMAZON } = FooterModalData;

const mainContainerWrapper = css({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  overflow: "auto",
});

const mainContainer = css({
  marginBottom: "0px !important",
  padding: "36px 32px 76px"
});
const headingTextDesktop = css({
  color: "#0E2B31",
  lineHeight: "48px",
  fontSize: "36px",
  fontWeight: "500"
});
const headingTextMobile = css({
  color: "#0E2B31",
  lineHeight: "40px",
  fontSize: "28px"
});
const fontWeight300 = css({
  fontWeight: 300
});
const descriptionText = css({
  color: "#0E2B31",
});

const BreadcrumbThemeWrapper = ({ children }) => {
  return (
    <Theme
      tokens={BreadcrumbTokens}
    >
      {children}
    </Theme>
  );
};

const Layout: React.FC<ILayoutProps> = (props) => {
  const {
    children,
    customComponent,
    mobileView,
    heading,
    description,
    isFooterDesktop,
    isFooterMobile,
    isHome,
    isWithoutFooter,
    breadcrumbData,
    marginBottom,
    isReturnsPage = false,
    returnStatus = "",
    width = '100%',
    padding = 'none',
    catalogSyncComponent, 
    isCatalogSyncInProgress
  } = props;

  const [mainContentTop, setMainContentTop] = useState<number>(0);
  const mainContentRef = useRef(null);
  
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === mainContentRef.current) {
          setMainContentTop(entry.contentRect.height+73);
        }
      }
    });

    if (mainContentRef.current) {
      resizeObserver.observe(mainContentRef.current);
    }

    return () => {
      if (mainContentRef.current) {
        resizeObserver.unobserve(mainContentRef.current);
      }
    };
  }, [window.innerWidth, window.innerHeight]);

  const mainContent = css({
    position: "absolute",
    top: mainContentTop,
    width: "100%",
    bottom: 0,
  });

  const mainContainerMarginBottom = css({
    marginBottom: `${marginBottom || "56px"}  !important`
  });

  return (
    <Column
      backgroundColor={ANTI_FLASH_WHITE}
      data-testid="layout-main-container"
      className={`${mainContainerWrapper} ${!isHome && !isWithoutFooter && mainContainerMarginBottom}`}
    >
      <Column
        className={mainContainer}
        data-testid="layout-header-container"
        ref={mainContentRef}
      >
        {mobileView ? (
          <Column alignmentVertical="center" spacingInset="none" data-testid="layout-mobile-content">
            {breadcrumbData?.length > 1 && <BreadcrumbThemeWrapper>
              <BreadcrumbGroup>
                {breadcrumbData.map(({ label, onClick }, index) => (
                  <Breadcrumb key={index} href="#" onClick={onClick}>
                    {label}
                  </Breadcrumb>
                ))}
              </BreadcrumbGroup>
            </BreadcrumbThemeWrapper>}
            <Row data-testid="layout-mobile-heading-wrapper" width="100%" alignmentHorizontal="justify" alignmentVertical="center" wrap="down">
              <Text
                data-testid="layout-mobile-heading"
                className={headingTextMobile}
              >
                {heading}
              </Text>
              {customComponent ? customComponent : null}
            </Row>
            { isReturnsPage && returnStatus && (
              <StatusProvider
                returnStatus={returnStatus}
                width={width}
                padding={padding}
              />
            )}
          </Column>
        ) : (
          <Column data-testid="layout-desktop-content">
            {breadcrumbData?.length > 1 && <BreadcrumbThemeWrapper>
              <BreadcrumbGroup>
                {breadcrumbData.map(({ label, onClick }, index) => (
                  <Breadcrumb key={index} href="#" onClick={onClick}>
                    {label}
                  </Breadcrumb>
                ))}
              </BreadcrumbGroup>
            </BreadcrumbThemeWrapper>}
            <Row width="100%" alignmentHorizontal="justify" alignmentVertical="center" spacing="none 600 none none" data-testid="layout-desktop-heading-row">
              <Text
                data-testid="layout-desktop-heading"
                className={`${headingTextDesktop}`}
              >
                {heading}
              </Text>
              {customComponent ? customComponent : null}
              </Row>
              {isReturnsPage && returnStatus && (
                <StatusProvider
                  returnStatus={returnStatus}
                  width={width}
                  padding={padding}
                />
              )}
          </Column>
        )}
        {isCatalogSyncInProgress ? catalogSyncComponent: null}
        {description && (
          <Text
            type="b200"
            fontFamily="amazonEmber"
            data-testid="layout-description-text"
            className={descriptionText}
          >
            {description}
          </Text>
        )}
      </Column>
      <Column
        className={mainContent}
        spacingInset={mobileView ? "none 400" : "none 500"}
        data-testid="layout-content-container"
        spacing="450"
        alignmentVertical="justify"
      >
        <Column spacing="none" spacingInset="none none 500" height={isHome && !mobileView && "100%"}
          data-testid="layout-children-container">{children}</Column>
        {isFooterMobile && mobileView && (
          <FooterMobile/>
        )}
        {isFooterDesktop && !mobileView && (
          <Column spacing="500" spacingInset="500 none 500" data-testid="layout-desktop-footer">
            <Theme
              tokens={{ dividerColor: buttonBackgroundColorSecondaryDefault }}
            >
              <Divider />
            </Theme>
            <Row alignmentHorizontal="justify" data-testid="layout-footer-content-row">
              <Row data-testid="layout-footer-links-row">
                {footerConstants.map(({ id, title }) => {
                  if (id !== footerConstants.length) {
                    return <Text key={id} data-testid={`layout-footer-link-${id}`}>{title}</Text>;
                  }
                })}
              </Row>
              <Text data-testid="layout-footer-brand-text">
                {SMARTBIZ_BY_AMAZON}
              </Text>
            </Row>
          </Column>
        )}
      </Column>
    </Column>
  );
};
export default Layout;
